export interface SiteContactItem {
  label: string;
  value: string;
  href?: string;
  icon?: string;
}

export interface SiteFriendLinkItem {
  name: string;
  url: string;
  description?: string;
  tags?: string[];
}

export interface SiteLabModuleItem {
  id: string;
  title: string;
  description: string;
  status?: string;
  href?: string;
}

export interface SiteMusicTrackItem {
  name: string;
  audioUrl: string;
  artist?: string;
  coverUrl?: string;
  lyricUrl?: string;
}

export interface SiteMusicConfig {
  enabled: boolean;
  title?: string;
  subtitle?: string;
  autoplay: boolean;
  tracks: SiteMusicTrackItem[];
}

export interface SiteSupportAuthorConfig {
  enabled: boolean;
  title: string;
  description: string;
  qrImage?: string;
  link?: string;
}

export interface SiteEffectsConfig {
  enabled: boolean;
  particlesEnabled: boolean;
  clickEffectsEnabled: boolean;
  mouseAttractionEnabled: boolean;
  waveEffectsEnabled: boolean;
  customScript?: string;
  items?: SiteEffectItem[];
}

export interface SiteEffectItem {
  id: string;
  name: string;
  description?: string;
  enabled: boolean;
  script?: string;
}

export interface SiteHomeHeroSlideAction {
  text: string;
  href: string;
}

export interface SiteHomeHeroSlideItem {
  title: string;
  subtitle?: string;
  description?: string;
  backgroundImage?: string;
  overlayColor?: string;
  textColor?: string;
  action?: SiteHomeHeroSlideAction;
}

export interface SiteHomeHeroConfig {
  enabled: boolean;
  autoplay: boolean;
  autoplayInterval: number;
  slides: SiteHomeHeroSlideItem[];
}

export interface SiteAboutContactItem {
  label: string;
  href: string;
}

export interface SiteAboutConfig {
  badge?: string;
  title: string;
  subtitle?: string;
  avatarText?: string;
  avatarImage?: string;
  nickname?: string;
  profileTitle?: string;
  profileDescription?: string;
  profileMeta?: string;
  storyTitle?: string;
  storyParagraphs: string[];
  goalsTitle?: string;
  goals: string[];
  contactsTitle?: string;
  contactsDescription?: string;
  contacts: SiteAboutContactItem[];
}

export interface SiteLive2DModelItem {
  id: string;
  name: string;
  modelJsonPath: string;
  previewImage?: string;
}

export interface SiteLive2DConfig {
  enabled: boolean;
  interactive: boolean;
  selectedModelId: string;
  models: SiteLive2DModelItem[];
  interactionTexts: string[];
}

export interface SiteConfig {
  contacts: SiteContactItem[];
  friendLinks: SiteFriendLinkItem[];
  labModules: SiteLabModuleItem[];
  music: SiteMusicConfig;
  supportAuthor: SiteSupportAuthorConfig;
  effects: SiteEffectsConfig;
  homeHero: SiteHomeHeroConfig;
  about: SiteAboutConfig;
  live2d: SiteLive2DConfig;
}

function normalizeAssetUrl(url?: string | null): string {
  const raw = String(url || "").trim();
  if (!raw) return "";
  if (/^http:\/\/[^\s"'<>]*aliyuncs\.com/i.test(raw)) {
    return raw.replace(/^http:\/\//i, "https://");
  }
  if (/^\/\/[^\s"'<>]*aliyuncs\.com/i.test(raw)) {
    return `https:${raw}`;
  }
  return raw;
}

const isProd = import.meta.env.PROD;
const runtimeOrigin = typeof window !== 'undefined' ? window.location.origin : '';
const API_BASE = (
  import.meta.env.VITE_API_URL ||
  (isProd
    ? import.meta.env.VITE_PROD_BASE_URL || runtimeOrigin
    : import.meta.env.VITE_DEV_BASE_URL || 'http://localhost:1337')
).replace(/\/parse\/?$/, '');

const DEFAULT_SITE_CONFIG: SiteConfig = {
  contacts: [],
  friendLinks: [],
  labModules: [],
  music: {
    enabled: false,
    title: "",
    subtitle: "",
    autoplay: false,
    tracks: [],
  },
  supportAuthor: {
    enabled: false,
    title: "支持作者",
    description: "",
    qrImage: "",
    link: "",
  },
  effects: {
    enabled: true,
    particlesEnabled: true,
    clickEffectsEnabled: true,
    mouseAttractionEnabled: true,
    waveEffectsEnabled: true,
    customScript: "",
    items: [
      { id: "particles", name: "粒子动画", description: "首页与背景粒子效果", enabled: true, script: "" },
      { id: "mouseAttraction", name: "鼠标吸引", description: "鼠标附近粒子吸附效果", enabled: true, script: "" },
      { id: "clickEffects", name: "点击特效", description: "点击涟漪/粒子反馈", enabled: true, script: "" },
      { id: "waveEffects", name: "波浪动画", description: "页面波浪视觉层", enabled: true, script: "" },
    ],
  },
  homeHero: {
    enabled: true,
    autoplay: true,
    autoplayInterval: 5000,
    slides: [],
  },
  about: {
    badge: "About",
    title: "关于我",
    subtitle: "",
    avatarText: "羽",
    nickname: "",
    profileTitle: "",
    profileDescription: "",
    profileMeta: "",
    storyTitle: "",
    storyParagraphs: [],
    goalsTitle: "",
    goals: [],
    contactsTitle: "",
    contactsDescription: "",
    contacts: [],
  },
  live2d: {
    enabled: true,
    interactive: true,
    selectedModelId: "shizuku",
    models: [
      {
        id: "shizuku",
        name: "Shizuku",
        modelJsonPath: "https://cdn.jsdelivr.net/npm/live2d-widget-model-shizuku@1.0.5/assets/shizuku.model.json",
      },
      {
        id: "haru-01",
        name: "Haru 01",
        modelJsonPath: "https://cdn.jsdelivr.net/npm/live2d-widget-model-haru@1.0.5/assets/haru01.model.json",
      },
      {
        id: "wanko",
        name: "Wanko",
        modelJsonPath: "https://cdn.jsdelivr.net/npm/live2d-widget-model-wanko@1.0.5/assets/wanko.model.json",
      },
    ],
    interactionTexts: ["今天也要开心写代码呀～", "点到我啦，要一起逛逛博客吗？", "有新文章记得第一时间看哦！"],
  },
};

export interface ContactMessagePayload {
  nickname: string;
  email: string;
  message: string;
}

export interface ContactMessageResult {
  id: string;
  pendingCount: number;
  remainingQuota: number;
  limit: number;
}

export interface MyContactMessageItem {
  id: string;
  nickname: string;
  email: string;
  message: string;
  isReplied: boolean;
  replyNote?: string;
  repliedAt?: string;
  createdAt: string;
}

export interface MyContactMessageSummary {
  limit: number;
  pendingCount: number;
  remainingQuota: number;
  messages: MyContactMessageItem[];
}

export async function submitContactMessage(payload: ContactMessagePayload): Promise<ContactMessageResult> {
  const response = await fetch(`${API_BASE}/api/blog/contact-messages`, {
    method: "POST",
    cache: "no-store",
    headers: {
      "Content-Type": "application/json",
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
    },
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data?.message || "留言提交失败");
  }

  return data;
}

export async function fetchMyContactMessages(): Promise<MyContactMessageSummary> {
  const response = await fetch(`${API_BASE}/api/blog/contact-messages/mine`, {
    method: "GET",
    cache: "no-store",
    headers: {
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
    },
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data?.message || "获取留言状态失败");
  }

  return data;
}

export async function fetchPublicSiteConfig(): Promise<SiteConfig> {
  try {
    const response = await fetch(`${API_BASE}/api/admin/site-config/public`, {
      cache: "no-store",
      headers: {
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
      },
    });
    if (!response.ok) {
      return DEFAULT_SITE_CONFIG;
    }

    const payload = await response.json();
    if (payload?.code !== 200 || !payload?.data) {
      return DEFAULT_SITE_CONFIG;
    }

    return {
      ...DEFAULT_SITE_CONFIG,
      ...payload.data,
      music: {
        ...DEFAULT_SITE_CONFIG.music,
        ...(payload.data.music || {}),
        tracks: Array.isArray(payload.data.music?.tracks)
          ? payload.data.music.tracks
              .map((item: SiteMusicTrackItem) => ({
                ...item,
                audioUrl: normalizeAssetUrl(item?.audioUrl),
                coverUrl: normalizeAssetUrl(item?.coverUrl) || undefined,
                lyricUrl: normalizeAssetUrl(item?.lyricUrl) || undefined,
              }))
              .filter((item: SiteMusicTrackItem) => !!item.audioUrl)
          : [],
      },
      supportAuthor: {
        ...DEFAULT_SITE_CONFIG.supportAuthor,
        ...(payload.data.supportAuthor || {}),
        qrImage: normalizeAssetUrl(payload.data.supportAuthor?.qrImage),
      },
      effects: {
        ...DEFAULT_SITE_CONFIG.effects,
        ...(payload.data.effects || {}),
        enabled: payload.data.effects?.enabled !== false,
        items: Array.isArray(payload.data.effects?.items)
          ? payload.data.effects.items.map((item: any) => ({
              id: String(item?.id || "").trim(),
              name: String(item?.name || "").trim() || "未命名特效",
              description: String(item?.description || "").trim() || undefined,
              enabled: item?.enabled !== false,
              script: String(item?.script || "").trim(),
            }))
          : DEFAULT_SITE_CONFIG.effects.items,
        customScript: String(payload.data.effects?.customScript || ""),
      },
      homeHero: {
        ...DEFAULT_SITE_CONFIG.homeHero,
        ...(payload.data.homeHero || {}),
        slides: Array.isArray(payload.data.homeHero?.slides)
          ? payload.data.homeHero.slides.map((item: SiteHomeHeroSlideItem) => ({
              ...item,
              backgroundImage: normalizeAssetUrl(item?.backgroundImage) || item?.backgroundImage,
            }))
          : [],
      },
      about: {
        ...DEFAULT_SITE_CONFIG.about,
        ...(payload.data.about || {}),
        storyParagraphs: Array.isArray(payload.data.about?.storyParagraphs) ? payload.data.about.storyParagraphs : [],
        goals: Array.isArray(payload.data.about?.goals) ? payload.data.about.goals : [],
        contacts: Array.isArray(payload.data.about?.contacts) ? payload.data.about.contacts : [],
      },
      live2d: {
        ...DEFAULT_SITE_CONFIG.live2d,
        ...(payload.data.live2d || {}),
        models: Array.isArray(payload.data.live2d?.models)
          ? payload.data.live2d.models
              .map((item: any) => ({
                id: String(item?.id || "").trim(),
                name: String(item?.name || "").trim() || "未命名模型",
                modelJsonPath: String(item?.modelJsonPath || "").trim(),
                previewImage: normalizeAssetUrl(item?.previewImage) || undefined,
              }))
              .filter((item: SiteLive2DModelItem) => !!item.id && !!item.modelJsonPath)
          : DEFAULT_SITE_CONFIG.live2d.models,
        interactionTexts: Array.isArray(payload.data.live2d?.interactionTexts)
          ? payload.data.live2d.interactionTexts.map((item: any) => String(item || "").trim()).filter(Boolean)
          : DEFAULT_SITE_CONFIG.live2d.interactionTexts,
      },
      contacts: Array.isArray(payload.data.contacts) ? payload.data.contacts : [],
      friendLinks: Array.isArray(payload.data.friendLinks) ? payload.data.friendLinks : [],
      labModules: Array.isArray(payload.data.labModules) ? payload.data.labModules : [],
    };
  } catch {
    return DEFAULT_SITE_CONFIG;
  }
}
